import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocationProvider } from './context/LocationContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ApplicationStatusPage from './pages/ApplicationStatusPage';
import DashboardPage from './pages/DashboardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import CampaignsPage from './pages/CampaignsPage';
import BookingsPage from './pages/BookingsPage';
import QRScannerPage from './pages/QRScannerPage';
import ProfilePage from './pages/ProfilePage';
import MenuPage from './pages/MenuPage';
import PackageSelectionPage from './pages/PackageSelectionPage';
import LocationsPage from './pages/LocationsPage';
import FloorPlanPage from './pages/FloorPlanPage';
import ReviewsPage from './pages/ReviewsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChainOverviewPage from './pages/ChainOverviewPage';

// Layout & Gate
import Layout from './components/layout/Layout';
import LockedFeaturePage from './components/LockedFeaturePage';

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
    <div style={{ width: '50px', height: '50px', border: '5px solid #f3f3f3', borderTop: '5px solid #f97316', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
    <h2 style={{ color: '#f97316' }}>Yüklənir...</h2>
  </div>
);

// Requires only auth (for package selection page)
const AuthRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading && !isAuthenticated) return <Spinner />;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Requires auth AND package selection
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, business } = useAuth();
  // Show spinner only if loading AND no cached session yet
  if (loading && !isAuthenticated) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!business?.premium_tier) return <Navigate to="/select-package" />;
  return children;
};

// Chain owner → chain-overview, others → dashboard
const DefaultRedirect = () => {
  const { business } = useAuth();
  return <Navigate to={business?.is_chain ? '/chain-overview' : '/dashboard'} />;
};

// Tier-gated route: shows LockedFeaturePage if tier is insufficient
// chainBypass: filial şəbəkəsi sahibi tier məhdudiyyəti olmadan keçir
const TierRoute = ({ children, minTier, featureName, chainBypass }) => {
  const { business } = useAuth();
  if (chainBypass && business?.is_chain) return children;
  const tier = business?.premium_tier || 1;
  if (tier < minTier) {
    return <LockedFeaturePage requiredTier={minTier} featureName={featureName} />;
  }
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LocationProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/application-status" element={<ApplicationStatusPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/select-package" element={<AuthRoute><PackageSelectionPage /></AuthRoute>} />

          {/* Private Routes - Layout ilə */}
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/menu" element={<MenuPage />} />
                    <Route path="/reviews" element={<ReviewsPage />} />

                    {/* Growth+ only (tier ≥ 2) */}
                    <Route path="/floor-plan" element={
                      <TierRoute minTier={2} featureName="Virtual Floor Plan">
                        <FloorPlanPage />
                      </TierRoute>
                    } />
                    <Route path="/bookings" element={
                      <TierRoute minTier={2} featureName="Rezervasiya sistemi">
                        <BookingsPage />
                      </TierRoute>
                    } />
                    <Route path="/campaigns" element={
                      <TierRoute minTier={2} featureName="Kampaniyalar & QR Kupon">
                        <CampaignsPage />
                      </TierRoute>
                    } />
                    <Route path="/scanner" element={
                      <TierRoute minTier={2} featureName="QR Skan">
                        <QRScannerPage />
                      </TierRoute>
                    } />
                    <Route path="/locations" element={
                      <TierRoute minTier={3} featureName="Çox Lokasiya" chainBypass>
                        <LocationsPage />
                      </TierRoute>
                    } />
                    <Route path="/chain-overview" element={
                      <TierRoute minTier={1} featureName="Filial İcmalı" chainBypass>
                        <ChainOverviewPage />
                      </TierRoute>
                    } />

                    <Route path="/" element={<DefaultRedirect />} />
                  </Routes>
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
        </LocationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Loading spinner animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default App;